// Lógica PURA do badge: rótulos, faixas, pluralização e o formato do payload.
//
// MORAL DE VER ESTE ARQUIVO: ele NÃO importa `mongodb` e não deve passar a
// importar. Qualquer componente `"use client"` precisa de `badgeMeta`,
// `badgeCounts` e do tipo `CancelBadgePayload` — e um `import` (mesmo de
// "só tipos") a partir de um módulo que puxa o driver do Mongo faz o webpack
// tentar bundlar `net`, `tls`, `child_process` e `fs/promises` para o browser:
//   Module not found: Can't resolve 'net'  (via lib/cancel-badge.ts)
// Foi exatamente assim que este arquivo nasceu. A regra é simples: texto e
// matemática ficam aqui; acesso ao banco fica em `lib/cancel-badge.ts`.

// Quem causou o encerramento de uma carona ou reserva. Gravado em `cancelledBy`
// no momento do cancelamento — sem ele não é possível distinguir "não compareceu"
// de "foi cancelado em cima", e o stat culpa o inocente. `system` cobre automação
// (PIX expirado, publicação não paga no prazo).
export type CancelActor = "passenger" | "driver" | "system"

// O badge mede uma unidade diferente por papel: a CARONA para o motorista, a
// RESERVA para o passageiro. Por isso a frase é separada — "Cancela carona
// raramente" seria factualmente errado para quem cancela reservas.
export type BadgePersona = "driver" | "passenger"

export type BadgeLevel = "green" | "blue" | "yellow" | "orange" | "red"

// Base mínima de amostra. Abaixo dela a taxa é ruído estatístico: quem cancelou
// 1 de 3 não é "motorista que cancela com frequência", é alguém com 3 caronas.
// O badge só aparece a partir da 6ª (MIN_BASE + 1); antes disso o produto mostra
// "dados insuficientes" com os contadores, em vez de um número que não sustenta
// conclusão nenhuma.
export const MIN_BASE = 5

export function cancelRate(cancelled: number, total: number): number | null {
  if (total <= 0) return null
  return cancelled / total
}

// Função PURA de taxa -> nível. Não conhece a base: classificar é responsabilidade
// de `classifyBadge`, que é quem tem o `total`. Mantê-las separadas permite
// testar as fronteiras da taxa isoladamente do critério de amostra mínima.
export function badgeLevel(rate: number | null): BadgeLevel | null {
  if (rate === null) return null
  if (rate === 0) return "green"
  if (rate <= 0.05) return "blue"
  if (rate <= 0.15) return "yellow"
  if (rate <= 0.3) return "orange"
  return "red"
}

export function hasSufficientBase(total: number): boolean {
  return total > MIN_BASE
}

export interface CancelStat {
  /** cancelamentos causados por THIS user / total comparável. */
  cancelledByMe: number
  total: number
  rate: number | null
  level: BadgeLevel | null
  /** true quando `total <= MIN_BASE`: a taxa existe mas não sustenta badge. */
  insufficientData: boolean
  /** unidade que este stat mede — define o texto do label. */
  persona: BadgePersona
}

// Aplica a base mínima SOBRE a classificação por taxa. `insufficientData` e
// `level: null` são coisas distintas: `level: null` já significava "sem
// histórico" e os consumidores escondiam o bloco inteiro. Agora o contrato é
// "level null + insufficientData" = mostrar o estado neutro com contadores.
export function classifyBadge(
  cancelled: number,
  total: number,
  persona: BadgePersona = "driver"
): CancelStat {
  const rate = cancelRate(cancelled, total)
  const sufficient = hasSufficientBase(total)
  return {
    cancelledByMe: cancelled,
    total,
    rate,
    level: sufficient ? badgeLevel(rate) : null,
    insufficientData: !sufficient,
    persona,
  }
}

export interface BadgeMeta {
  level: BadgeLevel | null
  label: string
  /** Cor de destaque: ponto, borda e fundo da pílula. */
  color: string
  /** Cor do TEXTO. Distinta de `color` de propósito — ver WCAG_TINT_NOTE. */
  textColor: string
  insufficientData: boolean
}

// WCAG_TINT_NOTE: `color` serve as 600 do Tailwind e NÃO pode ser usada no
// texto do badge. A pílula é `text-xs` (12px) e exige 4.5:1, mas as 600
// reprovam: green-600 = 3.30, yellow-600 = 2.94, orange-600 = 3.56. As 700
// passam (5.02 / 4.92 / 5.18). `lib/__tests__/cancel-badge.test.ts` mede o
// contraste de toda `textColor` e falha se alguma cair abaixo de 4.5 — se
// alguém trocar uma cor por "// combina mais", o teste reclama.
// REGRA DE COPY: os 10 rótulos estão no PRESENTE e no PLURAL. As duas
// decisões são uma só, e a tabela existia quebrada nas duas.
//
// Presente: o badge descreve um HÁBITO, não um evento. "Cancela caronas
// raramente" é o que se diz de alguém; "Cancelou caronas raramente" descreve
// uma vez só e ficaria nonsense num perfil que mostra o histórico inteiro.
// O green era o único no passado — "Nunca cancelou" do lado de "Cancela
// carona às vezes" — e é o green que a regressão de zero% protegia.
//
// Plural: o badge agrega um PERÍODO ("em 20 caronas, 1 cancelada"), então a
// unidade contada éplural em todas as frases. A conversão para plural foi
// feita meio a meio e deixou a tabela incoerente: green+blue em plural com
// yellow/orange/red no singular, e na coluna de passageiro green no singular
// com blue no plural. Isso não aparece em nenhuma tela (o número plural é
// certo em quase toda frase) e só aparece quando você lê as 10 de uma vez.
//
// O teste trava as duas: `rotulos estao todos no presente` e `cada persona
// usa a unidade dela no mesmo numero`.
const BADGE_META: Record<
  BadgeLevel,
  { driver: string; passenger: string; color: string; textColor: string }
> = {
  green: {
    driver: "Nunca cancela caronas",
    passenger: "Nunca cancela reservas",
    color: "#16a34a",
    textColor: "#15803d",
  },
  blue: {
    driver: "Cancela caronas raramente",
    passenger: "Cancela reservas raramente",
    color: "#2563eb",
    textColor: "#1d4ed8",
  },
  yellow: {
    driver: "Cancela caronas às vezes",
    passenger: "Cancela reservas às vezes",
    color: "#ca8a04",
    textColor: "#a16207",
  },
  orange: {
    driver: "Cancela caronas com frequência",
    passenger: "Cancela reservas com frequência",
    color: "#ea580c",
    textColor: "#c2410c",
  },
  red: {
    driver: "Cancela caronas quase sempre",
    passenger: "Cancela reservas quase sempre",
    color: "#dc2626",
    textColor: "#b91c1c",
  },
}

// Estado neutro: cinza-500 do Tailwind para o ponto, cinza-600 para o texto.
const INSUFFICIENT_COLOR = "#6b7280"
const INSUFFICIENT_TEXT = "#4b5563"
const INSUFFICIENT_LABEL = "Dados insuficientes"

// `badgeMeta(null)` NÃO retorna mais null: quem chama com level null quer
// exibir o estado neutro. Use `stat.insufficientData` para decidir.
export function badgeMeta(level: BadgeLevel | null, persona: BadgePersona = "driver"): BadgeMeta {
  if (!level) {
    return {
      level: null,
      label: INSUFFICIENT_LABEL,
      color: INSUFFICIENT_COLOR,
      textColor: INSUFFICIENT_TEXT,
      insufficientData: true,
    }
  }
  const meta = BADGE_META[level]
  return {
    level,
    label: persona === "passenger" ? meta.passenger : meta.driver,
    color: meta.color,
    textColor: meta.textColor,
    insufficientData: false,
  }
}

// "12 caronas, 3 canceladas" / "1 reserva, 1 cancelada".
// Cada campo pluraliza pelo PRÓPRIO valor. O badge do perfil já plugava
// "canceladas" pelo total e produzia "1 carona, 0 canceladas".
export function badgeCounts(payload: CancelBadgePayload): string {
  const unit = payload.persona === "passenger" ? "reserva" : "carona"
  const units = payload.total === 1 ? unit : `${unit}s`
  const cancelled = payload.cancelledByMe === 1 ? "cancelada" : "canceladas"
  return `${payload.total} ${units}, ${payload.cancelledByMe} ${cancelled}`
}

// Regra de VISIBILIDADE, viva aqui e não dentro do componente, para poder ser
// testada: `jest.config.js` só coleta `*.test.ts`, então uma decisão tomada
// dentro de um `.tsx` fica sem cobertura nenhuma.
//
// `total === 0` não renderiza NADA, mesmo com `insufficientData: true`. Quem
// nunca dirigiu e nunca reservou veria "Dados insuficientes · 0 caronas, 0
// canceladas" — ruído que parece bug. "Insuficiente" pressupõe histórico:
// 1 a 5 é pouco histórico; zero é nenhum histórico, e para zero o certo é não
// mostrar badge.
export function shouldShowBadge(payload: CancelBadgePayload | null | undefined): payload is CancelBadgePayload {
  return !!payload && payload.total > 0
}

// Serialização para o cliente. `badgeMeta` só formata o nível; quem monta o
// payload precisa levar `rate`/`total`/`cancelledByMe` junto para o contador.
// Sem anotação de retorno de propósito: `CancelBadgePayload` é definido como
// `ReturnType<typeof toBadgePayload>`, e anotar aqui criaria referência circular.
export function toBadgePayload(stat: CancelStat) {
  const meta = badgeMeta(stat.level, stat.persona)
  return {
    level: stat.level,
    label: meta.label,
    color: meta.color,
    textColor: meta.textColor,
    insufficientData: stat.insufficientData,
    rate: stat.rate,
    cancelledByMe: stat.cancelledByMe,
    total: stat.total,
    persona: stat.persona,
  }
}

export type CancelBadgePayload = ReturnType<typeof toBadgePayload>
